/**
 * Firestore access for the client catalogue.
 *
 *   clients/{clientId}                      ClientDoc
 *   clients/{clientId}/vehicles/{vehicleId} VehicleDoc
 *
 * Every query carries a limit (the rules refuse anything unbounded or over 100),
 * and every write carries the audit stamp the rules require.
 *
 * Reads go to the server and writes run in transactions, so a lost connection
 * shows an error instead of an empty offline cache or a save that never finishes.
 */
import {
  Timestamp,
  collection,
  collectionGroup,
  doc,
  getDocFromServer,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAfter,
  where,
  type DocumentData,
  type DocumentSnapshot,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { trackEvent } from '../analytics';
import { auth, db } from '../firebase';
import { ConflictError, NotFoundError } from './errors';
import type { ClientFields, VehicleFields } from './validation';

export const PAGE_SIZE = 25;

interface Audit {
  createdAt: Timestamp;
  createdBy: string;
  createdByEmail: string;
  updatedAt: Timestamp;
  updatedBy: string;
  updatedByEmail: string;
}

export interface Client extends ClientFields, Audit {
  id: string;
}

export interface Vehicle extends VehicleFields, Audit {
  id: string;
  clientId: string;
}

export interface Page<T> {
  items: T[];
  /** Pass back as `after` to load the next page; null when there is none. */
  next: QueryDocumentSnapshot | null;
}

const clientsCol = () => collection(db, 'clients');
const clientRef = (id: string) => doc(db, 'clients', id);
const vehiclesCol = (clientId: string) => collection(db, 'clients', clientId, 'vehicles');
const vehicleRef = (clientId: string, vehicleId: string) =>
  doc(db, 'clients', clientId, 'vehicles', vehicleId);

function currentUser(): { uid: string; email: string } {
  const user = auth.currentUser;
  if (!user?.email) throw new Error('You are signed out. Sign in again.');
  return { uid: user.uid, email: user.email };
}

function createStamp() {
  const { uid, email } = currentUser();
  return {
    createdAt: serverTimestamp(),
    createdBy: uid,
    createdByEmail: email,
    updatedAt: serverTimestamp(),
    updatedBy: uid,
    updatedByEmail: email,
  };
}

function updateStamp() {
  const { uid, email } = currentUser();
  return { updatedAt: serverTimestamp(), updatedBy: uid, updatedByEmail: email };
}

function toClient(snap: DocumentSnapshot): Client {
  return { id: snap.id, ...(snap.data() as Omit<Client, 'id'>) };
}

function toVehicle(snap: DocumentSnapshot): Vehicle {
  return {
    id: snap.id,
    clientId: snap.ref.parent.parent?.id ?? '',
    parts: {}, // vehicles saved before parts were tracked have no such field
    ...(snap.data() as Omit<Vehicle, 'id' | 'clientId' | 'parts'>),
  };
}

/** Runs a paged query: asks for one extra document to learn whether another page exists. */
async function page<T>(
  base: Query<DocumentData>,
  constraints: QueryConstraint[],
  after: QueryDocumentSnapshot | null | undefined,
  map: (s: QueryDocumentSnapshot) => T,
): Promise<Page<T>> {
  const all = [...constraints, ...(after ? [startAfter(after)] : []), limit(PAGE_SIZE + 1)];
  const snap = await getDocsFromServer(query(base, ...all));
  const docs = snap.docs.slice(0, PAGE_SIZE);
  return {
    items: docs.map(map),
    next: snap.docs.length > PAGE_SIZE ? (docs.at(-1) ?? null) : null,
  };
}

// ---------- Clients ----------

/** A–Z by last name, optionally filtered to last names starting with `search`. */
export function listClients(search: string, after?: QueryDocumentSnapshot | null): Promise<Page<Client>> {
  const prefix = search.trim().toLowerCase();
  const constraints: QueryConstraint[] = prefix
    ? [where('lastNameLower', '>=', prefix), where('lastNameLower', '<', `${prefix}`)]
    : [];
  constraints.push(orderBy('lastNameLower'));
  return page(clientsCol(), constraints, after, toClient);
}

export async function getClient(id: string): Promise<Client> {
  const snap = await getDocFromServer(clientRef(id));
  if (!snap.exists()) throw new NotFoundError('This client');
  return toClient(snap);
}

/** Other clients already using this email (Firestore cannot enforce uniqueness). */
export async function findClientsByEmail(email: string, exceptId?: string): Promise<Client[]> {
  const snap = await getDocsFromServer(query(clientsCol(), where('email', '==', email), limit(5)));
  return snap.docs.map(toClient).filter((c) => c.id !== exceptId);
}

export async function createClient(fields: ClientFields): Promise<string> {
  const ref = doc(clientsCol());
  await runTransaction(db, async (tx) => {
    tx.set(ref, { ...fields, ...createStamp() });
  });
  trackEvent('client_created');
  return ref.id;
}

/**
 * Saves only if nobody changed the client since `loadedUpdatedAt`;
 * otherwise throws ConflictError so the user can reload instead of overwriting.
 */
export async function updateClient(id: string, fields: ClientFields, loadedUpdatedAt: Timestamp) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(clientRef(id));
    if (!snap.exists()) throw new NotFoundError('This client');
    if (!(snap.get('updatedAt') as Timestamp).isEqual(loadedUpdatedAt)) throw new ConflictError();
    tx.update(clientRef(id), { ...fields, ...updateStamp() });
  });
}

/**
 * Deletes a client and all their vehicles (Firestore does not cascade). Admins only, per the rules.
 * The client document goes last, in the same transaction as the final vehicles.
 */
export async function deleteClient(id: string): Promise<void> {
  const MAX = 100; // the largest page the rules allow
  for (;;) {
    const vehicles = await getDocsFromServer(query(vehiclesCol(id), limit(MAX)));
    const last = vehicles.size < MAX;
    await runTransaction(db, async (tx) => {
      vehicles.docs.forEach((v) => tx.delete(v.ref));
      if (last) tx.delete(clientRef(id));
    });
    if (last) {
      trackEvent('client_removed');
      return;
    }
  }
}

// ---------- Vehicles ----------

export async function listVehicles(clientId: string): Promise<Vehicle[]> {
  const snap = await getDocsFromServer(query(vehiclesCol(clientId), orderBy('licenseRenewalDate'), limit(100)));
  return snap.docs.map(toVehicle);
}

export async function addVehicle(clientId: string, fields: VehicleFields): Promise<string> {
  const ref = doc(vehiclesCol(clientId));
  await runTransaction(db, async (tx) => {
    tx.set(ref, { ...fields, ...createStamp() });
  });
  trackEvent('vehicle_added');
  return ref.id;
}

export async function updateVehicle(
  clientId: string,
  vehicleId: string,
  fields: VehicleFields,
  loadedUpdatedAt: Timestamp,
) {
  const ref = vehicleRef(clientId, vehicleId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new NotFoundError('This vehicle');
    if (!(snap.get('updatedAt') as Timestamp).isEqual(loadedUpdatedAt)) throw new ConflictError();
    tx.update(ref, { ...fields, ...updateStamp() });
  });
}

export async function deleteVehicle(clientId: string, vehicleId: string): Promise<void> {
  await runTransaction(db, async (tx) => {
    tx.delete(vehicleRef(clientId, vehicleId));
  });
}

// ---------- Renewals ----------

export interface Renewal {
  vehicle: Vehicle;
  /** Null when the parent client could not be read (for example, deleted meanwhile). */
  client: Client | null;
}

/**
 * Vehicles across all clients whose renewal date is on or after `fromIso`, soonest first.
 * Needs the collection-group index on vehicles.licenseRenewalDate (infra/firestore.tf).
 */
export async function listRenewals(fromIso: string, after?: QueryDocumentSnapshot | null): Promise<Page<Renewal>> {
  const vehicles = await page(
    collectionGroup(db, 'vehicles'),
    [where('licenseRenewalDate', '>=', fromIso), orderBy('licenseRenewalDate')],
    after,
    toVehicle,
  );

  const clientIds = [...new Set(vehicles.items.map((v) => v.clientId))];
  const clients = new Map<string, Client | null>();
  await Promise.all(
    clientIds.map(async (id) => {
      const snap = await getDocFromServer(clientRef(id));
      clients.set(id, snap.exists() ? toClient(snap) : null);
    }),
  );

  return {
    items: vehicles.items.map((vehicle) => ({ vehicle, client: clients.get(vehicle.clientId) ?? null })),
    next: vehicles.next,
  };
}
